/**
 * Helpers zum Bauen der Aktivitaets-Liste aus TANSS-Rohdaten.
 *
 * Gemeinsam genutzt von interner Ticket-Detail-Ansicht und Kundenportal.
 */
import type { ActivityItem } from './activityFeed.types'

/** TANSS liefert Datumsfelder als Unix-Sekunden (number) oder ISO-String. */
export function tanssToIso(value: unknown): string | null {
  if (value == null || value === 0 || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Date(value * 1000).toISOString()
  }
  if (typeof value === 'string') {
    if (/^\d+$/.test(value.trim())) {
      const n = Number(value)
      if (Number.isFinite(n)) return new Date(n * 1000).toISOString()
    }
    const parsed = Date.parse(value)
    return Number.isFinite(parsed) ? new Date(parsed).toISOString() : null
  }
  return null
}

export function formatActivityDate(iso: string | null, opts?: { long?: boolean }): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (opts?.long) {
    return d.toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  }
  return d.toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  })
}

function pickString(obj: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = obj[k]
    if (typeof v === 'string' && v.trim().length > 0) return v
  }
  return ''
}

function pickNumber(obj: Record<string, unknown>, keys: string[]): number | null {
  for (const k of keys) {
    const v = obj[k]
    if (typeof v === 'number' && Number.isFinite(v)) return v
    if (typeof v === 'string' && v !== '') {
      const n = Number(v)
      if (Number.isFinite(n)) return n
    }
  }
  return null
}

function isInternal(entry: Record<string, unknown>): boolean {
  const flag = entry.internal
  return flag === true || flag === 1 || flag === '1' || flag === 'true'
}

const PLANNING_TYPE_LABELS: Record<string, string> = {
  SUPPORT:               'Leistung',
  APPOINTMENT_PROPOSAL:  'Termin-Vormerkung',
  APPOINTMENT_FIX:       'Termin',
  APPOINTMENT_PRIVATE:   'Termin',
  VACATION:              'Urlaub',
  ILLNESS:               'Krankheit',
  ABSENCE:               'Abwesenheit',
  STAND_BY:              'Bereitschaft',
  OVERTIME:              'Überstunden',
  CUSTOM:                'Benutzerdefiniert',
}

const LOCATION_LABELS: Record<string, string> = {
  OFFICE:   'Büro / Remote',
  CUSTOMER: 'Vor Ort',
  REMOTE:   'Remote',
}

export interface TanssHistoryRaw {
  comments?: Record<string, unknown>[]
  supports?: Record<string, unknown>[]
  mails?: Record<string, unknown>[]
  documents?: Record<string, unknown>[]
  screenshots?: Record<string, unknown>[]
}

export interface BuildActivitiesOptions {
  /** Wenn gesetzt, werden interne Eintraege gar nicht zurueckgegeben (Kundenportal). */
  excludeInternal?: boolean
  /** Zusaetzliche Quelle: TimeEntries aus Ticket-Stammdaten (nur intern relevant). */
  timeEntries?: Record<string, unknown>[]
}

/**
 * Baut die vereinheitlichte Aktivitaets-Liste aus dem TANSS-History-Payload
 * und sortiert sie chronologisch absteigend (neueste zuerst).
 */
export function buildActivitiesFromTanssHistory(
  history: TanssHistoryRaw | null | undefined,
  options: BuildActivitiesOptions = {},
): ActivityItem[] {
  const items: ActivityItem[] = []
  const h = history ?? {}

  for (const c of h.comments ?? []) {
    if (options.excludeInternal && isInternal(c)) continue
    const date = tanssToIso(c.date) ?? tanssToIso(c.createdAt) ?? tanssToIso(c.created)
    items.push({
      id: `comment-${(c.id as number | string | undefined) ?? items.length}`,
      kind: 'comment',
      date,
      title: pickString(c, ['title']),
      content: pickString(c, ['content', 'text']),
      author: pickString(c, ['employeeName', 'authorName', 'author', 'creatorName']) || null,
      internal: isInternal(c),
      meta: {},
      raw: c,
    })
  }

  for (const s of h.supports ?? []) {
    if (options.excludeInternal && isInternal(s)) continue
    const planningType = pickString(s, ['planningType']).toUpperCase()
    const isAppointment = planningType.startsWith('APPOINTMENT') && planningType !== 'APPOINTMENT_PRIVATE'
    const planningLabel = PLANNING_TYPE_LABELS[planningType] || (isAppointment ? 'Termin' : 'Leistung')
    const duration = pickNumber(s, ['duration', 'durationMinutes'])
    const date = tanssToIso(s.date) ?? tanssToIso(s.startTime) ?? tanssToIso(s.createdAt)
    const meta: Record<string, string> = {}
    if (duration != null && duration > 0) meta['Dauer'] = `${duration} Min.`
    const locationRaw = pickString(s, ['location'])
    if (locationRaw) meta['Ort'] = LOCATION_LABELS[locationRaw] ?? locationRaw
    const textIntern = pickString(s, ['textIntern'])
    if (textIntern && !options.excludeInternal) meta['Interne Bemerkung'] = textIntern
    items.push({
      id: `support-${(s.id as number | string | undefined) ?? items.length}`,
      kind: isAppointment ? 'appointment' : 'support',
      date,
      title: planningLabel,
      badgeLabel: planningLabel,
      content: pickString(s, ['text', 'content', 'description']),
      author: pickString(s, ['employeeName', 'technicianName']) || null,
      internal: isInternal(s),
      meta,
      raw: s,
    })
  }

  for (const m of h.mails ?? []) {
    if (options.excludeInternal && isInternal(m)) continue
    const sender = pickString(m, ['senderName']) || pickString(m, ['senderEmail']) || 'Unbekannt'
    const senderEmail = pickString(m, ['senderEmail'])
    const receiversArr = Array.isArray(m.receivers) ? (m.receivers as Record<string, unknown>[]) : []
    const receivers = receiversArr
      .map((r) => pickString(r, ['name']) || pickString(r, ['emailAddress']))
      .filter((s) => s.length > 0)
      .join(', ')
    const firstReceiverDate = receiversArr[0]?.['sentDate']
    const date = tanssToIso(m.date) ?? tanssToIso(firstReceiverDate) ?? tanssToIso(m.createdAt)

    const bodyPlain = pickString(m, ['bodyPlain'])
    const bodyHtml = pickString(m, ['bodyHtml'])
    const content = bodyPlain || (bodyHtml ? bodyHtml.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim() : '')

    const meta: Record<string, string> = {}
    if (sender) meta['Von'] = senderEmail ? `${sender} <${senderEmail}>` : sender
    if (receivers) meta['An'] = receivers
    const inbound = (m as { inbound?: unknown }).inbound
    if (typeof inbound === 'boolean') meta['Richtung'] = inbound ? 'Eingehend' : 'Ausgehend'

    items.push({
      id: `email-${(m.id as number | string | undefined) ?? items.length}`,
      kind: 'email',
      date,
      title: pickString(m, ['subject']) || 'E-Mail',
      content,
      author: sender,
      internal: isInternal(m),
      meta,
      raw: m,
    })
  }

  for (const d of h.documents ?? []) {
    if (options.excludeInternal && isInternal(d)) continue
    const date = tanssToIso(d.date) ?? tanssToIso(d.createdAt)
    const fileName = pickString(d, ['fileName', 'name'])
    const mime = pickString(d, ['mimeType'])
    items.push({
      id: `document-${(d.id as number | string | undefined) ?? items.length}`,
      kind: 'document',
      date,
      title: fileName || 'Dokument',
      content: pickString(d, ['description']) || (mime ? `${fileName} (${mime})` : fileName),
      author: pickString(d, ['employeeName', 'authorName']) || null,
      internal: isInternal(d),
      meta: mime ? { Typ: mime } : {},
      raw: d,
    })
  }

  for (const s of h.screenshots ?? []) {
    if (options.excludeInternal && isInternal(s)) continue
    const date = tanssToIso(s.date) ?? tanssToIso(s.createdAt)
    items.push({
      id: `image-${(s.id as number | string | undefined) ?? items.length}`,
      kind: 'image',
      date,
      title: pickString(s, ['fileName', 'name']) || 'Screenshot',
      content: pickString(s, ['description']) || pickString(s, ['fileName']) || 'Bild',
      author: pickString(s, ['employeeName', 'authorName']) || null,
      internal: isInternal(s),
      meta: {},
      raw: s,
    })
  }

  for (const te of options.timeEntries ?? []) {
    const start = pickString(te, ['startTime'])
    const end = pickString(te, ['endTime'])
    const duration = pickNumber(te, ['duration', 'durationMinutes']) ?? 0
    const date = tanssToIso(start)
    const fmt = (iso: string) => new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    const content = end
      ? `${fmt(start)} – ${fmt(end)} (${Math.floor(duration / 60)}h ${String(duration % 60).padStart(2, '0')}min)`
      : `Gestartet um ${start ? fmt(start) : '?'}`
    items.push({
      id: `service-${(te.id as number | string | undefined) ?? items.length}`,
      kind: 'service',
      date,
      title: pickString(te, ['description']) || 'Zeiteintrag',
      content,
      author: pickString(te, ['employeeName']) || null,
      internal: false,
      meta: {},
      raw: te,
    })
  }

  items.sort((a, b) => {
    const ta = a.date ? new Date(a.date).getTime() : 0
    const tb = b.date ? new Date(b.date).getTime() : 0
    return tb - ta
  })
  return items
}
